import { Box, FormHelperText, Tab, Tabs, Typography } from '@mui/material';

export type SegmentedTabOption = {
    value: string;
    label: string;
};

type SegmentedTabsProps = {
    label: string;
    value: string;
    options: SegmentedTabOption[];
    onChange: (value: string) => void;
    disabled?: boolean;
    helperText?: string;
};

export function SegmentedTabs({
    label,
    value,
    options,
    onChange,
    disabled = false,
    helperText,
}: SegmentedTabsProps) {
    const fallbackValue = options.some((option) => option.value === value) ? value : options[0]?.value ?? '';

    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontSize: '0.78rem', letterSpacing: '0.08em', fontWeight: 700 }}
            >
                {label}
            </Typography>
            <Tabs
                value={fallbackValue}
                onChange={(_event, nextValue: string) => onChange(nextValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    p: 0.5,
                    borderRadius: 999,
                    minHeight: 56,
                    border: '1px solid rgba(148, 163, 184, 0.28)',
                    background: 'linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.95) 100%)',
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTabs-scroller': { overflow: 'auto !important' },
                    '& .MuiTab-root': {
                        minHeight: 46,
                        borderRadius: 999,
                        px: 2.25,
                        mr: 0.5,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#475569',
                        transition: 'all 180ms ease',
                    },
                    '& .MuiTab-root.Mui-selected': {
                        color: '#0f172a',
                        background: 'linear-gradient(135deg, #bae6fd 0%, #7dd3fc 100%)',
                        boxShadow: '0 8px 16px rgba(2, 132, 199, 0.24)',
                    },
                    '& .MuiTab-root.Mui-disabled': {
                        opacity: 0.55,
                    },
                }}
            >
                {options.map((option) => (
                    <Tab key={option.value || 'all'} value={option.value} label={option.label} disabled={disabled} />
                ))}
            </Tabs>
            {helperText ? <FormHelperText sx={{ mt: 0.5 }}>{helperText}</FormHelperText> : null}
        </Box>
    );
}